import React, { useState } from 'react';
import { BPForm } from '@common/BPForm';
import { AuthField } from './Common/AuthField';
import { AuthLayout } from './Common/AuthLayout';
import { Button, Divider, Header, Message, Segment } from 'semantic-ui-react';
import * as yup from 'yup';
import { authClient } from './authClient';
import { LoginRecoveryRequest } from '@app/api2/api';
import { AuthLayoutRow } from './Common/AuthLayoutRow';
import { buildUrl, AppRoutes } from '../AppRoutes';
import { useTranslation2 } from '../Contexts/TranslationContext2';
import { BPButton } from '@/components/bp/bpbutton';

interface LoginRecoveryFormValues {
    recoveryCode: string;
}

export const LoginWithRecoveryCode: React.FC = () => {
    const { translations: t } = useTranslation2();
    const [loginError, setLoginError] = useState<string>('');

    const urlParams = new URLSearchParams(window.location.search);
    const returnUrl = urlParams.get('returnUrl') || '/';

    const initialValues: LoginRecoveryFormValues = {
        recoveryCode: ''
    };

    const validationSchema: yup.SchemaOf<LoginRecoveryFormValues> = yup.object().shape({
        recoveryCode: yup.string().label(t.auth.recoveryCode).trim()
            .required()
    });

    const handleSubmit = async (values: LoginRecoveryFormValues) => {
        setLoginError('');

        try {
            const request: LoginRecoveryRequest = {
                recoveryCode: values.recoveryCode
            };

            const result = await authClient.loginWithRecoveryCode(request);

            if (result.success) {
                window.location.assign(returnUrl);
            } else if (result.isLockedOut) {
                window.location.assign(buildUrl(AppRoutes.Lockout, {}));
            } else {
                setLoginError(result.errorMessage || 'Invalid recovery code entered.');
            }
        } catch (error) {
            console.error('Recovery code login error:', error);
            setLoginError('An error occurred during login. Please try again.');
        }
    };

    return (
        <AuthLayout header={t.auth.recoveryCodeVerification} description={t.auth.recoveryCodeLoginInfo}>
            <AuthLayoutRow>
                <Segment>
                    {loginError && (
                        <Message error>
                            {loginError}
                        </Message>
                    )}

                    <BPForm
                        initialValues={initialValues}
                        vaildationSchema={validationSchema}
                        onSubmit={handleSubmit}
                        hideButtons={true}
                    >
                        {(formProps) => (
                            <>
                                <AuthField
                                    name="recoveryCode"
                                    label={t.auth.recoveryCode}
                                    autoComplete="off"
                                    placeholder={t.auth.recoveryCodePlaceholder}
                                />

                                <Divider />

                                <BPButton
                                    className='bp-primary'
                                    type="submit"
                                    primary
                                    size="large"
                                    fluid
                                    disabled={formProps.isSubmitting}
                                    loading={formProps.isSubmitting}
                                >
                                    {t.account.login}
                                </BPButton>
                            </>
                        )}
                    </BPForm>
                </Segment>
            </AuthLayoutRow>
        </AuthLayout>
    );
};